import React, { useEffect, useState } from "react";
import { mergeStyles, Link } from "@fluentui/react";
import { Breadcrumb, IBreadcrumbItem } from "@fluentui/react/lib/Breadcrumb";
import { CustomizationEntry, CustomizationTab } from "../customizeEntries";
import { CustomizationEntrySelector } from "./customizationEntrySelector";
import { ContainerName } from "../../Redux/reducers/AppReducer";
import { OptionValueGetter } from "./optionValue";

const RESULTS_PAGE_SIZE = 50;

const resultsSummary = mergeStyles(
    {
        margin: '0 0 var(--druid-space-16)',
        color: 'var(--druid-color-text-secondary)',
    }
);

const searchResult = (isLast: boolean) => mergeStyles({
    marginBottom: isLast ? 0 : 'var(--druid-space-16)',
    borderBottom: isLast ? 'none' : '1px solid var(--druid-color-border)',
});

const resultBreadcrumb = mergeStyles(
    {
        margin: 0,
        '.ms-Breadcrumb-list': {
            alignItems: 'center',
            marginBottom: 'var(--druid-space-4)'
        },
        '.ms-Breadcrumb-listItem': {
            lineHeight: 1,
        },
        '.ms-Breadcrumb-itemLink, .ms-Breadcrumb-item': {
            height: 'auto',
            padding: '0 var(--druid-space-4)',
            fontSize: 'var(--druid-font-size-base)',
            fontWeight: 'var(--druid-font-weight-normal) !important',
            color: 'var(--druid-color-text-secondary) !important',
            lineHeight: 'var(--druid-line-height-tight)',
        },
        '.ms-Breadcrumb-listItem:first-child .ms-Breadcrumb-itemLink, .ms-Breadcrumb-listItem:first-child .ms-Breadcrumb-item': {
            marginLeft: 0,
            paddingLeft: 0,
        },
        '.ms-Breadcrumb-itemLink:hover': {
            backgroundColor: 'transparent',
            color: 'var(--druid-color-success-primary) !important',
        },
        '.ms-Breadcrumb-chevron': {
            fontSize: 'var(--druid-font-size-xs)',
            color: 'var(--druid-color-text-disabled)',
            margin: '0 var(--druid-space-2)',
        },
        '.ms-Breadcrumb-listItem:last-child .ms-Breadcrumb-item': {
            padding: 0,
            margin: 0,
        },
    }
);

const resultsLink = mergeStyles(
    {
        color: 'var(--druid-color-success-primary)',
        fontWeight: 'var(--druid-font-weight-semibold)',
        ':hover, :active, :focus': {
            color: 'var(--druid-color-success-hover)',
        },
    }
);

export interface OtherTabResult {
    tab: CustomizationTab;
    count: number;
}

interface Props {
    tab: CustomizationTab;
    query: string;
    results: CustomizationEntry[];
    otherTabResults: OtherTabResult[];
    getStyleOptionValue: OptionValueGetter;
    updateStyleElement: (styleElementName: string, value: any, containerName?: ContainerName) => void;
    onNavigate: (entry: CustomizationEntry, openSubCategory: boolean) => void;
    onSearchInTab: (tab: CustomizationTab) => void;
}

export const CustomizationSearchResults = (props: Props) => {
    const { tab, query, results, otherTabResults, getStyleOptionValue, updateStyleElement, onNavigate, onSearchInTab } = props;
    const [visibleCount, setVisibleCount] = useState(RESULTS_PAGE_SIZE);

    useEffect(() => {
        setVisibleCount(RESULTS_PAGE_SIZE);
    }, [query]);

    const renderOtherTabs = () => otherTabResults.length > 0 &&
        <p className={results.length ? resultsSummary : undefined}>
            {results.length ? 'Also found in ' : 'Found in '}
            {otherTabResults.map((result, index) => (
                <React.Fragment key={result.tab.key}>
                    {index > 0 && ', '}
                    <Link className={resultsLink} onClick={() => onSearchInTab(result.tab)}>{result.tab.text} ({result.count})</Link>
                </React.Fragment>
            ))}
        </p>;

    if (!results.length) {
        return (
            <div>
                <p><strong>No {tab.text} options match "{query.trim()}".</strong></p>
                {renderOtherTabs()}
            </div>
        );
    }

    const visibleResults = results.slice(0, visibleCount);

    const getBreadcrumbItems = (entry: CustomizationEntry): IBreadcrumbItem[] => {
        const items: IBreadcrumbItem[] = [{
            text: entry.category,
            key: `${entry.id}-category`,
            onClick: () => onNavigate(entry, false),
        }];

        if (entry.subCategory) {
            items.push({
                text: entry.subCategory,
                key: `${entry.id}-subcategory`,
                onClick: () => onNavigate(entry, true),
            });
        }

        return items;
    };

    return (
        <>
            <p className={resultsSummary}>{results.length} {results.length === 1 ? 'option' : 'options'}</p>
            {renderOtherTabs()}
            {visibleResults.map((entry, index) => (
                <div key={`${entry.containerName}.${entry.id}`} className={searchResult(index === visibleResults.length - 1)}>
                    <Breadcrumb
                        className={resultBreadcrumb}
                        items={getBreadcrumbItems(entry)}
                        ariaLabel={`Location of ${entry.displayName}`}
                        overflowIndex={0}
                        maxDisplayedItems={4}
                    />
                    <CustomizationEntrySelector
                        entry={entry}
                        value={getStyleOptionValue(entry.id, entry.containerName)}
                        onChange={updateStyleElement}
                        getStyleOptionValue={(key, containerName) => getStyleOptionValue(key, containerName || entry.containerName)}
                    />
                </div>
            ))}
            {results.length > visibleCount &&
                <p>
                    <Link className={resultsLink} onClick={() => setVisibleCount(results.length)}>Show all {results.length} options</Link>
                </p>}
        </>
    );
};

export default CustomizationSearchResults;
